import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * A labelled text input. The label is required rather than optional: a placeholder is not
 * a label, it disappears on focus, and the newsletter form in the supplied design is the
 * one place a reader types anything at all.
 *
 * Pass `labelHidden` when the design has no room for it — the label still exists for
 * screen readers, which is the accessibility floor in §2, not a nice-to-have.
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  labelHidden?: boolean;
  error?: string;
  hint?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, labelHidden = false, error, hint, className, ...props },
  ref,
) {
  const id = useId();
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          'text-xs font-bold uppercase tracking-wide text-ink',
          labelHidden && 'sr-only',
        )}
      >
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          'rounded-md border bg-white px-4 py-3 text-md text-ink outline-none transition',
          'placeholder:text-muted focus:border-red',
          error ? 'border-red' : 'border-line',
          className,
        )}
        {...props}
      />
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs font-semibold text-red" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
